import $http from "../../axios-instance";

/**
 * Listar los módulos.
 *
 * @param {*} commit - Función de Vuex para llamar a una mutación.
 * @return {Promise<void>}
 * @author Brandon Torres
 */
export async function getModules({ commit }) {
    commit("dashboard/load", true, { root: true });
    try {
        const response = await $http.get('forms/list-modules');
        commit("setModules", response.data.data);
    } catch (error) {
        if (this.$vue.$alertErrorsCallback(error)) return false;
        this.$vue.$alertError(error.message);
    } finally {
        commit("dashboard/load", false, { root: true });
    }
}

/**
 * Listar las categorías.
 *
 * @param {*} commit - Función de Vuex para llamar a una mutación.
 * @param {Integer} moduleId - Id del módulo.
 * @return {Promise<void>}
 * @author Brandon Torres
 */
export async function getCategories({ commit }, moduleId) {
    commit("dashboard/load", true, { root: true });
    try {
        const response = await $http.get(`forms/list-categories/${moduleId}`);
        commit("setCategories", response.data.data);
    } catch (error) {
        if (this.$vue.$alertErrorsCallback(error)) return false;
        this.$vue.$alertError(error.message);
    } finally {
        commit("dashboard/load", false, { root: true });
    }
}

/**
 * Listar las subcategorías.
 *
 * @param {*} commit - Función de Vuex para llamar a una mutación.
 * @param {Integer} categoryId - Id de la categoría.
 * @return {Promise<void>}
 * @author Brandon Torres
 */
export async function getSubcategories({ commit }, categoryId) {
    commit("dashboard/load", true, { root: true });
    try {
        const response = await $http.get(`forms/list-subcategories/${categoryId}`);
        commit("setSubcategories", response.data.data);
    } catch (error) {
        if (this.$vue.$alertErrorsCallback(error)) return false;
        this.$vue.$alertError(error.message);
    } finally {
        commit("dashboard/load", false, { root: true });
    }
}

/**
 * Guardar el formulario dinamico.
 *
 * @param {*} commit - Función de Vuex para llamar a una mutación.
 * @param {Object} formBuilder - Datos del formulario dinamico.
 * @return {Promise<void>} - Respuesta de la petición.
 * @author Brandon Torres
 */
export async function saveFormBuilder({ commit }, formBuilder) {
    commit("dashboard/load", true, { root: true });
    try {
        const response = await $http.post('forms/save-form-builder', formBuilder);
        this.$vue.$alertSuccess(response.data.message);
        return true;
    } catch (error) {
        if (this.$vue.$alertErrorsCallback(error)) return false;
        this.$vue.$alertError(error.message);
    } finally {
        commit("dashboard/load", false, { root: true });
    }
}

/**
 * Listar los formularios dinamicos.
 *
 * @param {*} commit - Función de Vuex para llamar a una mutación.
 * @param {*} state - Estado de las categorías de SGA.
 * @param reloadPage - Recargar la página.
 * @return {Promise<void>} - Respuesta de la petición.
 * @author Brandon Torres
 */
export async function getFormBuilders({ commit, state }, reloadPage = false) {
    commit("dashboard/load", true, { root: true });
    try {
        const { page, per_page, name, form_module_id, form_category_id } = state.filterForms;

        const response = await $http.get('forms/list-forms', {
            params: {
                page: reloadPage ? 1 : page,
                perPages: per_page,
                search: name,
                form_module_id,
                form_category_id,
            },
        });
        commit("setFormBuilders", response.data.data);
        commit("setPaginateForms", response.data.data);
    } catch (error) {
        if (this.$vue.$alertErrorsCallback(error)) return false;
        this.$vue.$alertError(error.message);
    } finally {
        commit("dashboard/load", false, { root: true });
    }
}

/**
 * Cambiar el estado del formulario dinamico.
 *
 * @param {*} commit - Función de Vuex para llamar a una mutación.
 * @param {Integer} formId - Id del formulario dinamico.
 * @return {Promise<void>} - Respuesta de la petición.
 * @author Brandon Torres
 */
export async function changeStatus({ commit }, formId) {
    commit("dashboard/load", true, { root: true });
    try {
        const response = await $http.put(`forms/change-status/${formId}`);
        this.$vue.$alertSuccess(response.data.message);
        return true;
    } catch (error) {
        if (this.$vue.$alertErrorsCallback(error)) return false;
        this.$vue.$alertError(error.message);
    } finally {
        commit("dashboard/load", false, { root: true });
    }
}

/**
 * Buscar un formulario por su id.
 *
 * @param {*} commit - Función de Vuex para llamar a una mutación.
 * @param {Integer} formId - Id del formulario dinamico.
 * @return {Promise<void>} - Respuesta de la petición.
 * @author Brandon Torres
 */
export async function showById({ commit }, formId) {
    commit("dashboard/load", true, { root: true });
    try {
        const response = await $http.get(`forms/show-form/${formId}`);
        commit("setFormBuilder", response.data.data);
        return true;
    } catch (error) {
        if (this.$vue.$alertErrorsCallback(error)) return false;
        this.$vue.$alertError(error.message);
        return false;
    } finally {
        commit("dashboard/load", false, { root: true });
    }
}

/**
 * Actualizar el formulario dinamico.
 *
 * @param {*} commit - Función de Vuex para llamar a una mutación.
 * @param {Object} formBuilder - Datos del formulario dinamico.
 * @return {Promise<void>} - Respuesta de la petición.
 * @author Brandon Torres
 */
export async function updateFormBuilder({ commit }, formBuilder) {
    commit("dashboard/load", true, { root: true });
    try {
        const response = await $http.put(`forms/update-form-builder/${formBuilder.id}`, formBuilder);
        this.$vue.$alertSuccess(response.data.message);
        return true;
    } catch (error) {
        if (this.$vue.$alertErrorsCallback(error)) return false;
        this.$vue.$alertError(error.message);
        return false;
    } finally {
        commit("dashboard/load", false, { root: true });
    }
}

/**
 * Listar las categorías por nombre de módulo.
 *
 * @param {*} commit - Función de Vuex para llamar a una mutación.
 * @param {String} codeModule - Código del módulo.
 * @return {Promise<void>}
 * @author Brandon Torres
 */
export async function getCategoriesForModule({ commit }, codeModule) {
    commit("dashboard/load", true, { root: true });
    try {
        const response = await $http.get(`forms/list-categories/for-name-module/${codeModule}`);
        commit("setCategoriesForModule", response.data.data);
    } catch (error) {
        if (this.$vue.$alertErrorsCallback(error)) return false;
        this.$vue.$alertError(error.message);
    } finally {
        commit("dashboard/load", false, { root: true });
    }
}

/**
 * Listar los formularios dinamicos por categoría.
 *
 * @param {*} commit - Función de Vuex para llamar a una mutación.
 * @param {Integer} category_id - Id de la categoría.
 * @return {Promise<void>} - Respuesta de la petición.
 * @author Brandon Torres
 */
export async function getFormByCategory({ commit }, category_id) {
    commit("dashboard/load", true, { root: true });
    try {
        const response = await $http.get(`forms/form-by-category/${category_id}`);
        commit("setFormByCategory", response.data.data);
    } catch (error) {
        if (this.$vue.$alertErrorsCallback(error)) return false;
        this.$vue.$alertError(error.message);
    } finally {
        commit("dashboard/load", false, { root: true });
    }
}

/**
 * Guardar los datos del formulario.
 *
 * @param {*} commit - Función de Vuex para llamar a una mutación.
 * @param {Object} payload - Datos del formulario.
 * @return {Promise<void>} - Respuesta de la petición.
 * @author Brandon Torres
 */
export async function saveDataForm({ commit }, payload) {
    commit("dashboard/load", true, { root: true });
    try {
        const response = await $http.post(`forms/save-data-form/${payload.company_id}`, payload);
        this.$vue.$alertSuccess(response.data.message);
        return true;
    } catch (error) {
        if (this.$vue.$alertErrorsCallback(error)) return false;
        this.$vue.$alertError(error.message);
        return false;
    } finally {
        commit("dashboard/load", false, { root: true });
    }
}

/**
 * Actualizar los datos del formulario.
 *
 * @param {*} commit - Función de Vuex para llamar a una mutación.
 * @param {Object} payload - Datos del formulario.
 * @return {Promise<void>} - Respuesta de la petición.
 * @author Brandon Torres
 */
export async function updateDataForm({ commit }, payload) {
    commit("dashboard/load", true, { root: true });
    try {
        const response = await $http.put(`forms/update-data-form/${payload.submission_id}/${payload.company_id}`, payload);
        this.$vue.$alertSuccess(response.data.message);
        return true;
    } catch (error) {
        if (this.$vue.$alertErrorsCallback(error)) return false;
        this.$vue.$alertError(error.message);
        return false;
    } finally {
        commit("dashboard/load", false, { root: true });
    }
}

/**
 * Listar los datos de un formulario.
 *
 * @param {*} commit - Función de Vuex para llamar a una mutación.
 * @param {Object} payload - Datos del formulario.
 * @return {Promise<void>} - Respuesta de la petición.
 * @author Brandon Torres
 */
export async function getFormSubmissions({ commit }, payload) {
    commit("dashboard/load", true, { root: true });
    try {
        const response = await $http.get(`forms/form-submissions/${payload.company_id}`, {
            params: {
                page: payload.page,
                per_page: payload.per_page,
                form_form_id: payload.form_form_id,
            }
        });
        return response.data.data;
    } catch (error) {
        if (this.$vue.$alertErrorsCallback(error)) return false;
        this.$vue.$alertError(error.message);
        return false;
    } finally {
        commit("dashboard/load", false, { root: true });
    }
}

/**
 * Obtener los datos de un formulario por su id.
 *
 * @param {*} commit - Función de Vuex para llamar a una mutación.
 * @param {Integer} form_submission_id - Id de los datos del formulario.
 * @param {Integer} company_id - Id de la empresa.
 * @return {Promise<void>} - Respuesta de la petición.
 * @author Brandon Torres
 */
export async function getFormSubmissionById({ commit }, { form_submission_id, company_id }) {
    commit("dashboard/load", true, { root: true });
    try {
        const response = await $http.get(`forms/form-submission/${company_id}/${form_submission_id}`);
        return response.data.data;
    } catch (error) {
        if (this.$vue.$alertErrorsCallback(error)) return false;
        this.$vue.$alertError(error.message);
        return false;
    } finally {
        commit("dashboard/load", false, { root: true });
    }
}

/**
 * Cambiar el estado de los datos de un formulario.
 *
 * @param {*} commit - Función de Vuex para llamar a una mutación.
 * @param {Integer} form_submission_id - Id de los datos del formulario.
 * @param {Integer} company_id - Id de la empresa.
 * @return {Promise<void>} - Respuesta de la petición.
 * @author Brandon Torres
 */
export async function changeStatusSubmission({ commit }, { form_submission_id, company_id }) {
    commit("dashboard/load", true, { root: true });
    try {
        const response = await $http.put(`forms/change-status-submission/${company_id}/${form_submission_id}`);
        this.$vue.$alertSuccess(response.data.message);
        return true;
    } catch (error) {
        if (this.$vue.$alertErrorsCallback(error)) return false;
        this.$vue.$alertError(error.message);
        return false;
    } finally {
        commit("dashboard/load", false, { root: true });
    }

}

/**
 * Listar los datos de un formulario por módulo.
 *
 * @param {*} commit - Función de Vuex para llamar a una mutación.
 * @param {Integer} company_id - Id de la empresa.
 * @param {String} code_module - Código del módulo.
 * @return {Promise<void>} - Respuesta de la petición.
 * @autor Brandon Torres
 */
export async function submissionsForModule({ commit }, { company_id, code_module }) {
    commit("dashboard/load", true, { root: true });
    try {
        const response = await $http.get(`forms/submissions-for-module/${company_id}/${code_module}`);
        switch (code_module) {
            case 'epps':
                commit("setSubmissionsEpps", response.data.data);
                break;
            default:
                break;
        }
    } catch (error) {
        if (this.$vue.$alertErrorsCallback(error)) return false;
        this.$vue.$alertError(error.message);
    } finally {
        commit("dashboard/load", false, { root: true });
    }
}
